import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Input,
  Table,
  Typography,
  message,
  Space,
  Dropdown,
  Popover,
  Checkbox,
  Divider,
} from "antd";
import { useGetUsimOrdersByStatusMutation } from "../../../../api/usim/orders/UsimOrdersApi";
import OrderDetailModal from "../../shared/orderDetailModal";
import { UsimOrderItemData } from "../../../../api/usim/orders/UsimOrderItem";
import { getCommonColumns, groupOrdersByOrderNumber, searchOrders, exportToCSV } from "../../shared/tableUtils";
import { SettingOutlined, ExportOutlined, AppstoreOutlined } from "@ant-design/icons";
import { formatDateISO } from "../../../../utils/date";
import { USimStatus, fromUSimStatus } from "../../shared/usimUtils";

const { Title } = Typography;

const BASE_COLUMNS = ["orderProcessedDate", "OrderNumber", "type", "sku", "iccid", "mobileNumber","status", "activationDate", "processedBy", "createdAt", "action"];
const COLUMNS_STORAGE_KEY = "usimActivationReportTableColumns";

export default function UsimManageOrderPage() {
  const [tableData, setTableData] = useState<UsimOrderItemData[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<UsimOrderItemData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : BASE_COLUMNS;
    } catch (e) {
      return BASE_COLUMNS;
    }
  });

  const handleColumnVisibilityChange = (checkedValues: any[]) => {
    setVisibleColumns(checkedValues as string[]);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(checkedValues));
  };

  const resetColumns = () => {
    setVisibleColumns(BASE_COLUMNS);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(BASE_COLUMNS));
  };

  const showDetailsModal = (order: UsimOrderItemData) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Get common columns and add action column
  const allColumns = [
    {
      title: "Order Processed Date",
      dataIndex: "orderProcessedDate",
      key: "orderProcessedDate",
      render: (text: string, record: UsimOrderItemData) => record.orderProcessedDate ? formatDateISO(record.orderProcessedDate) : "-",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => a.orderProcessedDate.localeCompare(b.orderProcessedDate),
    },
    {
      title: "Order Number",
      dataIndex: "OrderNumber",
      key: "OrderNumber",
      onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
      render: (text: string, record: UsimOrderItemData) => record.status?.toUpperCase() === 'CANCELLED' ? (
        <div style={{ backgroundColor: 'red', color: 'white', padding: '4px', borderRadius: '4px' }}>
          {record.OrderNumber || "-"}
        </div>
      ) : record.OrderNumber || "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
    },
    {
      title: "ICCID/SN",
      dataIndex: "iccid",
      key: "iccid",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: string, record: UsimOrderItemData) => fromUSimStatus(record.status),
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      render: (text: string, record: UsimOrderItemData) => {
        return record.activationDate ? formatDateISO(record.activationDate) : "-";
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => text ? formatDateISO(text) : "-",
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => (a.createdAt || "").localeCompare(b.createdAt || ""),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string, record: UsimOrderItemData) => (record.updatedAt || record.updated_at) ? formatDateISO(record.updatedAt || record.updated_at) : "-",
    },
    {
      title: "Shop ID",
      dataIndex: "shopId",
      key: "shopId",
    },
    {
      title: "Plan Name",
      dataIndex: "planName",
      key: "planName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Activated Date",
      dataIndex: "activatedDate",
      key: "activatedDate",
      render: (text: string) => text ? formatDateISO(text) : "-",
    },
    {
      title: "Processed By",
      dataIndex: "processedBy",
      key: "processedBy",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: UsimOrderItemData) => (
        <Button onClick={() => showDetailsModal(record)}>
          View Details
        </Button>
      )
    }
  ];

  const columns = allColumns.filter(col => visibleColumns.includes(col.key as string));

  const columnOptions = allColumns.map(col => ({
    label: col.title as string,
    value: col.key as string,
  }));

  const [getOrdersByStatus, { data: orders = { items: [], count: 0, status_filters: [] }, error, isLoading }] = useGetUsimOrdersByStatusMutation();

  const loadOrders = async () => {
    try {
      const result = await getOrdersByStatus({
        status_list: [USimStatus.COMPLETE]
      }).unwrap();
      if (result?.items) {
        const groupedData = groupOrdersByOrderNumber(result.items);
        setTableData(groupedData);
      } else {
        setTableData([]);
      }
    } catch (err) {
      message.error('Failed to fetch orders');
      setTableData([]);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleExport = () => {
    try {
      exportToCSV(tableData);
      message.success(`Exported ${tableData.length} orders successfully`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
    }
  };

  const optionsMenuItems = [
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: handleExport,
    }
  ];

  const processedData = searchOrders(tableData, searchQuery);

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Title level={2}>Activation Report</Title>
      <Flex justify="space-between" align="center">
        <Button type="primary" onClick={loadOrders}>
          Reload
        </Button>
        <Space>
          <Input.Search
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all fields"
            style={{ width: 300 }}
          />
          <Dropdown menu={{ items: optionsMenuItems }} trigger={['hover']}>
            <Button icon={<SettingOutlined />}>
              Options
            </Button>
          </Dropdown>
          <Popover
            content={
              <div style={{ maxWidth: 300 }}>
                <Checkbox.Group
                  options={columnOptions}
                  value={visibleColumns}
                  onChange={handleColumnVisibilityChange}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Button type="link" onClick={resetColumns} style={{ padding: 0 }}>
                  Reset to Default
                </Button>
              </div>
            }
            title="Visible Columns"
            trigger="click"
            placement="bottomRight"
          >
            <Button icon={<AppstoreOutlined />}>
              Columns
            </Button>
          </Popover>
        </Space>
      </Flex>

      {error && (
        <Alert
          type="error"
          message="Error loading orders"
          description="Failed to fetch order data. Please try again."
        />
      )}

      <Table
        loading={isLoading}
        dataSource={processedData}
        rowSelection={rowSelection}
        columns={columns}
        rowKey={(record) => record.OrderNumber + record.shopId}
        scroll={{ x: 'max-content' }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} orders`
        }}
      />

    {isModalVisible && (  <OrderDetailModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        orderData={selectedOrder}
      />)}
    </Flex>
  );
}
